import { useTheme } from '@mui/material/styles'
import { useTranslate } from 'ra-core'
import {
  ArrayField,
  ChipField,
  DateField,
  Filter,
  FunctionField,
  List,
  RecordRepresentation,
  SelectColumnsButton,
  SingleFieldList,
  WithListContext,
  WrapperField,
} from 'react-admin'

import config from './config'
import vehiclesExporter from './exporter'
import { VehicleIssueFilter, VehicleLockFilter, VehicleTypeFilter } from './filter'
import VehicleFormLayout, { transformValues } from './form'
import AdvancedBooleanFilter from '../../components/AdvancedBooleanFilter'
import AdvancedDatagridConfigurable from '../../components/AdvancedDatagridConfigurable'
import AdvancedSearchFilter from '../../components/AdvancedSearchFilter'
import AdvancedTextField from '../../components/AdvancedTextField'
import CompactList from '../../components/CompactList'
import NoteField from '../../components/NoteField'
import ReferenceValue from '../../components/ReferenceValue'
import StaticText from '../../components/StaticText'
import StatusField from '../../components/StatusField'
import UnitField from '../../components/UnitField'
import VehicleIconField from '../../components/VehicleIconField'
import VehicleLockField from '../../components/VehicleLockField'
import { COMMON_INPUT_WIDTH } from '../../config/theme'
import { getStatusStyles } from '../../domain/statuses'
import { isVehicleInstalled } from '../../domain/vehicles'
import { useCurrentAccountSelectors, useSingleOrganisationFilterDefaultValues } from '../accounts/hooks'
import { AdvancedCreateButton } from '../common/create'
import { AdvancedEditInDialogButton } from '../common/edit'
import { GenericListLayout, ListActions } from '../common/list'
import HubField from '../hubs/field'
import HubsFilter from '../hubs/filter'
import OrganisationField from '../organisations/field'
import OrganisationsFilter from '../organisations/filter'

const VehiclesFilters = (props) => {
  const translate = useTranslate()
  return (
    <Filter {...props}>
      <AdvancedSearchFilter
        alwaysOn
        placeholder={translate('resources.vehicles.forms.helpers.searchPlaceholder')}
        sx={{ '&&': { width: COMMON_INPUT_WIDTH * 1.5 } }}
      />
      <OrganisationsFilter alwaysOn />
      <HubsFilter alwaysOn />
      <VehicleTypeFilter alwaysOn />
      <AdvancedBooleanFilter source="active" alwaysOn />
      <VehicleLockFilter alwaysOn />
      <VehicleIssueFilter alwaysOn />
    </Filter>
  )
}

const vehicleRowSx = (record) => {
  const isInstalled = isVehicleInstalled(record)
  return { opacity: isInstalled ? 1 : 0.4 }
}

const DTCSField = ({ source }) => {
  return (
    <ArrayField source={source}>
      <SingleFieldList linkType={false}>
        <FunctionField render={(record) => <ChipField record={{ code: record }} source={'code'} />} />
      </SingleFieldList>
    </ArrayField>
  )
}

export const VehiclesListLayout = ({ title }) => {
  const { hasSingleOrganisation, hasSingleHub } = useCurrentAccountSelectors()
  const theme = useTheme()
  const translate = useTranslate()

  return (
    <GenericListLayout
      title={title}
      compactListLayout={
        <CompactList
          linkType="show"
          itemSx={vehicleRowSx}
          icon={<VehicleIconField />}
          iconBadgeColor={(record) => getStatusStyles(record.status, theme).mainColor}
          title={<RecordRepresentation />}
          body={() => (
            <>
              <StaticText>{`${translate('resources.organisations.name', 1)}: `}</StaticText>
              <ReferenceValue reference="organisations" />
              <br />
              <StaticText>{`${translate('resources.hubs.name', 1)}: `}</StaticText>
              <ReferenceValue reference="hubs" />
            </>
          )}
        />
      }
      regularListLayout={
        <AdvancedDatagridConfigurable bulkActionButtons={false} rowClick="show" rowSx={vehicleRowSx}>
          <StatusField source="status" />
          {!hasSingleOrganisation && <OrganisationField sortable={false} />}
          {!hasSingleHub && <HubField sortable={false} />}
          <VehicleIconField source="type" color="textSecondary" />
          <AdvancedTextField source="brand" />
          <AdvancedTextField source="model" />
          <AdvancedTextField source="designation" />
          <UnitField source="autonomy" />
          <UnitField source="battery_voltage" formatValue={(v) => v.toFixed(1)} unit="V" />
          <UnitField source="mileage" />
          <UnitField source="next_maintenance_mileage" />
          <DateField source="next_maintenance_date" emptyText="n/a" />
          <UnitField source="remaining_oil_life" formatValue={(v) => v.toFixed(0)} />
          <DTCSField source="dtcs" />
          <VehicleLockField source="locked" />
          <NoteField source="notes" sortable={false} />
          <WrapperField source={translate('ra.action.edit')} label={false} sortable={false}>
            <AdvancedEditInDialogButton transform={transformValues}>
              <VehicleFormLayout />
            </AdvancedEditInDialogButton>
          </WrapperField>
        </AdvancedDatagridConfigurable>
      }
    />
  )
}

export default () => {
  const filterDefaultValues = useSingleOrganisationFilterDefaultValues(config.options.defaultFilterValues)

  return (
    <List
      empty={false}
      sort={config.options.defaultSort}
      filters={<VehiclesFilters />}
      filterDefaultValues={filterDefaultValues}
      exporter={vehiclesExporter}
      actions={
        <ListActions hasExport>
          <WithListContext
            render={({ filterValues }) => (
              <AdvancedCreateButton transform={transformValues} defaultValues={filterValues}>
                <VehicleFormLayout />
              </AdvancedCreateButton>
            )}
          />
          <SelectColumnsButton />
        </ListActions>
      }
    >
      <VehiclesListLayout />
    </List>
  )
}
